import { CaretDown, CaretUp } from "@phosphor-icons/react";
import Card from "../Card";
import Typography from "../Typography";
import { format } from "date-fns";
import Avatar from "../Avatar";
import { getInitialsFromDisplayName } from "../../utils/getInitialsFromDisplayNames";
import Table, { TableHeaderConfig } from "../Table";
import { CarbonCalculation } from "../../api/models/ActivityData";
import { formatNumber } from "../../utils/formatNumber";
import { useState } from "react";

export type ActivityDataCalculationCardProps = {
    rawUnitsSuffixHtml: string;
    rawMaterialName: string;
    submittedAt: string;
    rawActivityQuantity: number;
    co2e?: number;
    createdByUserDisplayName?: string;
    normalizedUnitsSuffixHtml: string;
    carbonCalculations: CarbonCalculation[];
}

const ActivityDataCalculationCard = ({
    rawMaterialName,
    submittedAt,
    createdByUserDisplayName,
    rawUnitsSuffixHtml,
    rawActivityQuantity,
    co2e,
    carbonCalculations
}: ActivityDataCalculationCardProps) => {

    const headings: TableHeaderConfig[] = ["Calculation Period", "Total Period %", "Raw Subquantity", "CF", "Normalised Subquantity", "EF", "CO2e"]
        .map(label => ({ label }));

    const [showTable, setShowTable] = useState(true);

    const handleCaretClick = () => setShowTable(show => !show);

    return <Card
        variant={"success"}
        className="flex-1 p-6 max-w-full overflow-hidden"
    >
        <Typography variant="title-small" component="h2" className="pb-1 border-b border-inherit">
            Raw Activity Data
        </Typography>
        <div className="flex flex-row border-b border-inherit items-center py-2">
            <Typography
                variant="display-small"
                component="p"
                className="text-soft-green-80 flex-auto"
            >
                {formatNumber(rawActivityQuantity)}{" "}
                <span
                    dangerouslySetInnerHTML={{
                        __html: rawUnitsSuffixHtml,
                    }}
                />
                <span>{` ${rawMaterialName} `}</span>

            </Typography>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                <Typography variant="label-medium" component="p">
                    Submitted by
                </Typography>
                <Typography variant="label-medium" component="p">
                    Date
                </Typography>
                <div className="flex items-center gap-3">
                    <Avatar
                        initials={getInitialsFromDisplayName(
                            createdByUserDisplayName
                        )}
                        variant="teal"
                    />
                    <Typography variant="body-large" component="p">
                        {createdByUserDisplayName}
                    </Typography>
                </div>

                <Typography variant="body-large" component="p" className="flex items-center">
                    {format(new Date(submittedAt), "dd/MM/yyyy")}
                </Typography>
            </div>
        </div>
        <Card variant={"neutral"} className="mt-5 p-6">
            <div className="pb-1 border-b border-inherit flex justify-between">
                <Typography variant="title-small" component="h2">
                    Carbon emissions
                </Typography>
                {!showTable 
                        ? <CaretUp size={20} weight="light" fill="" className="cursor-pointer" onClick={handleCaretClick}/>
                        : <CaretDown size={20} weight="light" fill="" className="cursor-pointer" onClick={handleCaretClick}/>
                }
            </div>
                <Typography
                        variant="display-small"
                        component="p"
                        className="mt-2"
                    >
                        {formatNumber(co2e)} kg CO<sub>2</sub>e
                    </Typography>
            {showTable &&
            <>
                <Typography
                    variant="title-small"
                    component="h3"
                    className="py-2 mt-2 border-t border-inherit"
                >
                    Calculations
                </Typography>
                <Table
                    headings={headings}
                    data={carbonCalculations.map((calculation, index) => ({
                        id: index,
                        ...calculation,
                    }))}
                    className="!max-w-full !min-w-full"
                    renderRowCells={(calculation) => {
                        return (
                            <>
                                <td className="!min-w-[50px]">
                                    {format(new Date(calculation.startDate), "dd/MM/yyyy")} - {format(new Date(calculation.endDate), "dd/MM/yyyy")}
                                </td>
                                <td className="!max-w-[40px]">
                                    {formatNumber(calculation.rawActivityQuantity * 100 / rawActivityQuantity)} %
                                </td>
                                <td className="!min-w-[40px]">
                                    {formatNumber(calculation.rawActivityQuantity)}
                                </td>
                                <td>
                                    {formatNumber(calculation.conversionFactor)}
                                </td>
                                <td className="!min-w-[50px]">
                                    {formatNumber(calculation.normalizedActivityQuantity)}
                                </td>
                                <td>
                                    {formatNumber(calculation.emissionFactor)}
                                </td>
                                <td>
                                    {formatNumber(calculation.cO2e)}
                                </td>
                            </>
                        );
                    }}
                />
            </>}
        </Card>
    </Card>;
};

export default ActivityDataCalculationCard;